import type { Payment } from '@paid-ui/models/payment';
import type { PaymentSchedule } from '@paid-ui/types/payment-schedule';
import type { PaymentStage } from '@paid-ui/types/payments';

import { sortBy } from 'lodash';

import { paymentStageMap } from '@paid-ui/constants/payments';
import { ClaimMethod, ClaimSource, ClaimStatus, ClaimType } from '@paid-ui/enums/claim';
import {
  ContractRequirementStatus,
  ContractRequirementType,
  ContractState,
} from '@paid-ui/enums/contract';
import { EvidenceCategory } from '@paid-ui/enums/evidence';
import { DefaultPaymentActionType, PaymentSecuredStatus } from '@paid-ui/enums/payment';
import { ContractAction, RequiringActionFeatureType } from '@paid-ui/enums/requiring-action';
import { SupercontractCode } from '@paid-ui/enums/supercontract';
import { WorkItemType } from '@paid-ui/enums/work-item';
import { filterPaymentRequiringAction } from '@paid-ui/models/payment';
import { projectManager } from '@paid-ui/models/project';
import { userManager } from '@paid-ui/models/user';
import { userGroupManager } from '@paid-ui/models/user-group';
import { getClaimPeriodName, isFrequencyBasedSchedule } from '@paid-ui/utils/claim-period';
import { utc2Mel } from '@paid-ui/utils/datetime';
import { isNotNil } from '@paid-ui/utils/filters';
import { mergeClaimHistories } from '@paid-ui/utils/histories';
import { getStageName } from '@paid-ui/utils/payments';

import { contractManager } from '../model';

export const getDisplayStageName = (
  payment?: PaymentStage,
  isSuperContract?: boolean,
  paymentSchedule?: PaymentSchedule | null,
) => {
  if (!payment) {
    return 'Unknown';
  }

  const { source, workItemPayment, type } = payment.claim ?? {};

  if (type === ClaimType.RETENTION) {
    return workItemPayment?.name || 'Retention';
  }

  if (workItemPayment) {
    const endDate = workItemPayment.endDate;

    if (source === ClaimSource.OFF_PLATFORM) {
      return getStageName(payment);
    }

    // A frequency-based claim shows the name it was submitted with, which defaults to its period and
    // may have been edited; before submission it has none, so the period stands in.
    if (isFrequencyBasedSchedule(paymentSchedule)) {
      return (
        workItemPayment.name ||
        getClaimPeriodName(paymentSchedule, workItemPayment.startDate, endDate) ||
        'Periodic Claim'
      );
    }

    const defaultName = workItemPayment.name || 'Periodic Claim';
    return endDate ? utc2Mel(endDate).format('MMMM') : defaultName;
  }

  if (isSuperContract) {
    return payment.customName ?? paymentStageMap[payment.stage];
  }

  return getStageName(payment);
};

interface RecallApprovedClaimInput {
  impersonation: boolean;
  disableContractActions: boolean;
  canManageContract: boolean;
  roleActions?: readonly string[];
  isDraftContract: boolean;
  isContractExecuted: boolean;
  hasAllSigned: boolean;
  claimState?: ClaimStatus;
  isRetention: boolean;
  isOffPlatform: boolean;
  isNonContractualParty: boolean;
  isConsolidatedClaim: boolean;
  isStaged: boolean;
  isPaid: boolean;
  releasedUnpaid: number;
  retained: number;
  hasInflight: boolean;
}

// A Paid admin, impersonating the approver / assessor, can recall an approved claim until payment is released, scheduled or paid.
export const canRecallApprovedClaim = (input: RecallApprovedClaimInput) =>
  input.impersonation &&
  !input.disableContractActions &&
  input.canManageContract &&
  [ContractAction.CLAIM_APPROVE, ContractAction.CLAIM_ASSESS].some(
    (action) => input.roleActions?.includes(action) ?? false,
  ) &&
  !input.isDraftContract &&
  input.isContractExecuted &&
  input.hasAllSigned &&
  input.claimState === ClaimStatus.APPROVED &&
  !input.isRetention &&
  !input.isOffPlatform &&
  !input.isNonContractualParty &&
  !input.isConsolidatedClaim &&
  !input.isStaged &&
  !input.isPaid &&
  input.releasedUnpaid === 0 &&
  input.retained === 0 &&
  !input.hasInflight;

export const formatProgressPayment = (
  payment: PaymentStage,
  options: {
    index: number;
    isProvisional?: boolean;
    snapshot?: Record<string, any>;
  },
): Payment => {
  const { index, isProvisional = false, snapshot = null } = options;
  const { amount = 0, secured, claim, parent, linked, description } = payment;

  const { impersonation, disableContractActions } = userManager;
  const { permission } = userGroupManager;
  const { primeContract, requiringActions: projectRequiringActions } = projectManager;
  const { canManageContract, canManagePayment } = permission;

  const paymentHistories = claim?.paymentHistory ?? [];
  const claimHistories = claim?.stateHistory ?? [];

  const histories = mergeClaimHistories(claim);

  const {
    id: contractId,
    contractName,
    fullContractName,
    suburbAndState,
    contractType,
    paymentStructure,
    formattedAddress,
    templateCode,
    hasAllSigned,

    price,
    costOfWorks,
    totalNumberOfPayments,
    numberOfClaimedPayments,
    gstFree,
    gstRate,
    priceIncludeGst,

    partyName,
    currentParty,
    theOtherPartyName,
    theOtherParty,
    isPayer,
    isPayee,
    isInviter,
    isInvitee,
    isNonContractualParty,
    isSuperintendent,
    isArchitect,
    payeeParty,
    payerParty,
    inviterParty,
    inviteeParty,
    participantParties,
    superintendentParty,
    architectParty,
    theOtherReviewParty,
    qss,

    statuses,
    requirements,
  } = contractManager;

  const {
    isPeriodic,
    isSuperContract,
    isDraftContract,
    isPrimeContract,
    isServiceContract,
    isContractExecuted,
    isOneLastPaymentLeft,
  } = statuses;

  const isPrimeContractExecuted = primeContract?.contractState === ContractState.EXECUTED;
  const {
    workItemPaymentNumber = '',
    workItemPaymentLines = [],
    endDate = '',
  } = payment.claim?.workItemPayment ?? {};

  const displayName = getDisplayStageName(
    payment,
    isSuperContract,
    contractManager.paymentSchedule,
  );
  const lineWorkItems = workItemPaymentLines.filter(
    ({ workItem }) => workItem.type === WorkItemType.LINE,
  );
  const variationWorkItems = workItemPaymentLines.filter(
    ({ workItem }) => workItem.type === WorkItemType.VARIATION,
  );

  const hasLinks = isNotNil(linked) && linked.length > 0;
  const hasParent = Boolean(parent?.id);
  const hasClaim = Boolean(claim?.id);
  const claimAmount = claim?.amount ?? 0;
  const claimState = claim?.state;
  const claimSource = claim?.source;
  const variations = claim?.variations ?? [];
  const adjustments = claim?.adjustments ?? [];
  const numberOfVariations = variations.length;
  const numberOfAdjustments = adjustments.length;
  const dueDate = claim?.dueDate;
  const dueInDays = claim?.dueInDays ?? 0;
  const paymentTermsInDays = claim?.paymentTermsInDays ?? 0;
  const earlyReleaseDiscountRate = claim?.earlyReleaseDiscountRate;
  const breakdown = claim?.paymentBreakdown;
  const earlyReleaseRequests = claim?.earlyReleaseRequests ?? [];
  const {
    securedUnreleased = 0,
    releasedUnpaid = 0,
    totalHistoryPaid = 0,
    discount = 0,
    totalPayable,
    unsecuredUnpaid = 0,
    inflight = 0,
  } = breakdown ?? {};
  const isRetention = claim?.type === ClaimType.RETENTION;

  const totalPaid = totalHistoryPaid + discount;
  const payableTotal = isPeriodic ? (totalPayable?.amount ?? 0) : claimAmount;

  const isOffPlatform = claim?.source === ClaimSource.OFF_PLATFORM;
  const isApproved =
    claim?.state === ClaimStatus.APPROVED || claim?.state === ClaimStatus.PRE_APPROVED;
  const isAssessed =
    !!claim?.state &&
    [ClaimStatus.APPROVED, ClaimStatus.PRE_APPROVED, ClaimStatus.PAID].includes(claim.state);
  const isOverdue = dueInDays < 0;

  const hasInflight = inflight > 0;
  const isFullPaid = claim?.state === ClaimStatus.PAID;
  const isPartPaid = !isFullPaid && totalPaid > 0;
  const isPaid = isPartPaid || isFullPaid;

  const isFullySecured = claimAmount
    ? !isPaid && securedUnreleased + releasedUnpaid >= payableTotal
    : secured === PaymentSecuredStatus.SECURED;

  const isPartSecured = claimAmount
    ? !isPaid && !isFullySecured && securedUnreleased > 0
    : secured === PaymentSecuredStatus.PARTIALLY_SECURED;
  const isSecured = isPartSecured || isFullySecured;

  const claimTags = claim?.tags ?? [];
  const isConsolidatedClaim = claimTags.includes('CONSOLIDATED');
  const requiringActions = projectRequiringActions.filter((ra) =>
    filterPaymentRequiringAction(ra, payment),
  );

  const depositInsuranceRequired = requirements.some(
    ({ status, type }) => status === 'PENDING' && type === 'DEPOSIT_INSURANCE',
  );
  const isDepositPayment = payment.tags?.includes('DEPOSIT') ?? false;

  // When no claim is created, payee can only claim for deposit stage.
  const canClaimDepositOnly =
    !isPrimeContract ||
    numberOfClaimedPayments > 0 ||
    !depositInsuranceRequired ||
    (numberOfClaimedPayments === 0 && isDepositPayment);

  // When only last payment is left unapproved, payee can claim for last payment.
  const canClaimFinalPayment =
    isOneLastPaymentLeft || (!isOneLastPaymentLeft && index !== totalNumberOfPayments - 1);

  const canSecurePayment = requirements.some(
    (requirement) =>
      requirement.type === ContractRequirementType.LEGACY_SECURE_FUNDS_AGREEMENT &&
      requirement.status === ContractRequirementStatus.FULFILED,
  );

  const canClaim =
    !disableContractActions &&
    canManageContract &&
    isPayee &&
    !isDraftContract &&
    !isPeriodic &&
    isContractExecuted &&
    hasAllSigned &&
    !hasClaim &&
    canClaimDepositOnly &&
    canClaimFinalPayment;

  const canEdit =
    !disableContractActions &&
    canManageContract &&
    isPayee &&
    claim?.state === ClaimStatus.PROVISIONAL &&
    isContractExecuted;

  const canUpdate =
    impersonation && !isDraftContract && isContractExecuted && hasAllSigned && isConsolidatedClaim;

  const canLink =
    !disableContractActions &&
    canManagePayment &&
    isPayer &&
    !isDraftContract &&
    !isPrimeContract &&
    !isPeriodic &&
    isPrimeContractExecuted &&
    isContractExecuted &&
    hasAllSigned &&
    !hasClaim &&
    claim?.type !== ClaimType.RETENTION &&
    !hasParent &&
    !isFullySecured;

  const canSecureBase =
    templateCode !== SupercontractCode.PAID_DEED_SECUREPAID_2025 &&
    templateCode !== SupercontractCode.OFF_PLATFORM_HEAD_DEPOSIT_CONTRACT_2025 &&
    templateCode !== SupercontractCode.OFF_PLATFORM_FUNDING_PERIODIC_2025 &&
    canSecurePayment &&
    canManagePayment &&
    isPayer &&
    !isNonContractualParty &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned;

  const canSecureStaged =
    !disableContractActions &&
    canSecureBase &&
    !isPeriodic &&
    !hasClaim &&
    !hasParent &&
    !isFullPaid &&
    (secured === PaymentSecuredStatus.NOT_SECURED ||
      secured === PaymentSecuredStatus.PARTIALLY_SECURED);

  const canSecurePeriodic =
    !disableContractActions &&
    canSecureBase &&
    isPeriodic &&
    claim?.type !== ClaimType.RETENTION &&
    isAssessed &&
    !isOffPlatform &&
    !isFullPaid &&
    unsecuredUnpaid > 0 &&
    (secured === PaymentSecuredStatus.NOT_SECURED ||
      secured === PaymentSecuredStatus.PARTIALLY_SECURED);

  const canSecureNotAssessed =
    !disableContractActions &&
    canSecureBase &&
    isPeriodic &&
    !isOffPlatform &&
    claim?.type !== ClaimType.RETENTION &&
    !!claim?.state &&
    [
      ClaimStatus.PROVISIONAL,
      ClaimStatus.SUBMITTED,
      ClaimStatus.REJECTED,
      ClaimStatus.RESUBMITTED,
    ].includes(claim.state);

  const canSecure = canSecureStaged || canSecurePeriodic || canSecureNotAssessed;

  const canRelease =
    !disableContractActions &&
    templateCode !== SupercontractCode.OFF_PLATFORM_FUNDING_PERIODIC_2025 &&
    canManagePayment &&
    !isPrimeContract &&
    isPayee &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    claim?.type !== ClaimType.RETENTION &&
    claimState === ClaimStatus.APPROVED &&
    !isFullPaid &&
    Number(dueInDays) > 0 &&
    isNotNil(earlyReleaseDiscountRate) &&
    (secured === PaymentSecuredStatus.SECURED ||
      secured === PaymentSecuredStatus.PARTIALLY_SECURED);

  const canMarkAsPaidBase =
    canManagePayment &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    isApproved &&
    !isConsolidatedClaim;

  const canMarkAsPaidUser =
    canMarkAsPaidBase && !disableContractActions && isPayee && isOffPlatform;
  const canMarkAsPaidAdmin = canMarkAsPaidBase && impersonation;
  const canMarkAsPaid = canMarkAsPaidUser || canMarkAsPaidAdmin;

  const canUpdateClaimAmountBase =
    canManagePayment &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    (isApproved || isFullPaid) &&
    !isConsolidatedClaim;
  const canUpdateClaimAmount = canUpdateClaimAmountBase && impersonation;

  const canPay =
    canManagePayment &&
    isApproved &&
    claim.source !== ClaimSource.OFF_PLATFORM &&
    Boolean(payeeParty?.account) &&
    unsecuredUnpaid > 0 &&
    !isFullySecured;

  const canResubmit =
    !disableContractActions && canManageContract && claim?.state === ClaimStatus.RESUBMITTED;

  const canRecall =
    !disableContractActions &&
    canManageContract &&
    isPayee &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    !isOffPlatform &&
    !isNonContractualParty &&
    !!claim?.state &&
    [ClaimStatus.SUBMITTED, ClaimStatus.RESUBMITTED].includes(claim.state) &&
    !isConsolidatedClaim;

  const canRecallApproved = canRecallApprovedClaim({
    impersonation: Boolean(impersonation),
    disableContractActions,
    canManageContract,
    roleActions: currentParty?.role?.actions,
    isDraftContract,
    isContractExecuted,
    hasAllSigned,
    claimState,
    isRetention,
    isOffPlatform,
    isNonContractualParty,
    isConsolidatedClaim,
    isStaged: paymentStructure === ClaimMethod.STAGED,
    isPaid,
    releasedUnpaid,
    // claim-service refuses once retention has been taken from the claim
    retained: breakdown?.retention?.retained ?? 0,
    hasInflight,
  });

  const canReview =
    !disableContractActions &&
    canManageContract &&
    claim?.type === ClaimType.RETENTION &&
    (isSuperintendent || isPayer) &&
    !!claim?.state &&
    [ClaimStatus.SUBMITTED, ClaimStatus.RESUBMITTED].includes(claim?.state);

  const hasRequestAssessmentRA = requiringActions.some(
    (ra) => ra.featureItemType === RequiringActionFeatureType.CLAIM_ASSESSMENT_REQUEST,
  );

  const canRequestQSReport =
    !disableContractActions &&
    canManagePayment &&
    currentParty?.role?.actions?.includes(ContractAction.CLAIM_ASSESSMENT_REQUEST_SUBMIT) &&
    qss.length > 0 &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    claim?.state !== ClaimStatus.PROVISIONAL;

  const canSendQSReport =
    !disableContractActions &&
    canManagePayment &&
    currentParty?.role?.actions?.includes(ContractAction.CLAIM_ASSESSMENT_REPORT_SUBMIT) &&
    !isDraftContract &&
    isContractExecuted &&
    hasAllSigned &&
    hasClaim &&
    claim?.state !== ClaimStatus.PROVISIONAL;

  const earlyReleaseDiscounts = (claim?.discounts ?? []).filter(
    (discount) => discount.type === 'EARLY_RELEASE_DISCOUNT',
  );

  const evidences = sortBy(claim?.evidences || [], (evidence) => evidence.fileName);

  const displayAmount = hasClaim ? claimAmount : amount;

  return {
    ...payment,
    id: payment.id ?? '',
    displayName,
    displayAmount,
    hasLinks,
    hasParent,
    hasClaim,
    description,
    claimId: claim?.id ?? '',
    claimAmount: claimAmount,
    taxableAmount: claim?.taxableAmount ?? {
      amount: 0,
      excludeGstAmount: 0,
      gstAmount: 0,
    },
    retentionAmount: claim?.retentionAmount ?? claim?.retentionTaxableAmount?.amount ?? 0,
    retentionTaxableAmount: claim?.retentionTaxableAmount ?? {
      amount: 0,
      excludeGstAmount: 0,
      gstAmount: 0,
    },
    claimState: claimState,
    claimSource: claimSource,
    dueDate,
    dueInDays,
    disabled: !canClaim,
    submissionDueDate: isServiceContract ? undefined : claim?.submissionDueDate,
    consolidatedClaimEndDate: endDate,
    workItemPayment: claim?.workItemPayment,
    workItemPaymentNumber,
    lineWorkItems,
    variationWorkItems,
    paymentTermsInDays,
    earlyReleaseDiscountRate,
    earlyReleaseRequests,
    breakdown,
    adjustments,
    variations,
    numberOfVariations: numberOfVariations + numberOfAdjustments,
    numberOfLinkedPayments: linked?.length ?? 0,
    earlyReleaseDiscounts,
    numberOfDiscounts: earlyReleaseDiscounts.length,
    claimHistories,
    paymentHistories,
    histories,
    evidences,
    invoices: evidences.filter((evidence) => evidence.category === EvidenceCategory.INVOICE),
    documents: evidences.filter((evidence) => evidence.category === EvidenceCategory.DOCUMENT),
    photosAndVideos: evidences.filter((evidence) =>
      [EvidenceCategory.PHOTOGRAPH, EvidenceCategory.VIDEO].includes(evidence.category),
    ),
    contract: {
      id: contractId,
      contractType,
      paymentStructure,
      templateCode,
      gstSettings: {
        gstFree,
        gstRate,
        priceIncludeGst,
      },
      contractName,
      fullContractName,
      suburbAndState,
      price,
      costOfWorks,
      address: formattedAddress,
      partyName,
      party: currentParty,
      theOtherPartyName,
      theOtherParty,
      theOtherReviewParty,
      isPayer,
      isPayee,
      isInviter,
      isInvitee,
      isSuperintendent,
      isArchitect,
      payerParty,
      payeeParty,
      inviteeParty,
      inviterParty,
      participantParties,
      superintendentParty,
      architectParty,
      isContractExecuted,
    },
    requiringActions,
    defaultActions: [
      {
        type: DefaultPaymentActionType.CLAIM,
        text: 'Claim',
        hidden: !canClaim,
      },
      {
        type: DefaultPaymentActionType.UPDATE,
        text: 'Update',
        hidden: !canUpdate,
      },
      {
        type: DefaultPaymentActionType.LINK,
        text: 'Link',
        hidden: !canLink,
      },
      {
        type: DefaultPaymentActionType.RELEASE,
        text: 'Get InstaPaid',
        hidden: !canRelease,
      },
      {
        type: DefaultPaymentActionType.RECALL,
        text: 'Recall',
        hidden: !canRecall && !canRecallApproved,
      },
      {
        type: DefaultPaymentActionType.REQUEST_ASSESSMENT,
        text: 'Request QS report',
        hidden: !canRequestQSReport,
      },
      {
        type: DefaultPaymentActionType.CLAIM_ASSESSMENT,
        text: 'Claim assessment',
        hidden: !canSendQSReport || hasRequestAssessmentRA,
      },
    ],
    dropdownActions: [
      {
        type: DefaultPaymentActionType.MARK_AS_PAID,
        text: 'Mark as paid',
        hidden: !canMarkAsPaid,
      },
      {
        type: DefaultPaymentActionType.UPDATE_CLAIM_AMOUNT,
        text: 'Update claim amount',
        hidden: !canUpdateClaimAmount,
      },
    ],
    statuses: {
      isRetention,
      isPeriodic,
      isOffPlatform,
      isApproved,
      isProvisional,
      isAssessed,
      isFullPaid,
      isFullySecured,
      isOverdue,
      isPaid,
      isPartPaid,
      isPartSecured,
      isSecured,
      hasInflight,
    },
    permissions: {
      canClaim,
      canUpdate,
      canLink,
      canMarkAsPaid,
      canRelease,
      canSecure,
      canRecall,
      canRecallApproved,
      canResubmit,
      canReview,
      canPay,
      canEdit,
    },
    snapshot,
  };
};
